import React, { useEffect, useState, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import { MapContainer, TileLayer, useMap, LayersControl } from 'react-leaflet';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import 'leaflet-gpx';
import L from 'leaflet';

import MarkerCluster from './MarkerCluster';

const TravelSearchMap = (props) => {
  const { BaseLayer } = LayersControl;
  const { latitude, longitude, zoom } =
    drupalSettings.travel_search_map_settings;

  const [skibusRoutes, setSkibusRoutes] = useState([]);
  const [showAllBusRoutes, setShowAllBusRoutes] = useState(false);
  const [visibleBusRoutes, setVisibleBusRoutes] = useState([]);

  const layerGroupRef = useRef(null);

  const [topRoute, setTopRoute] = useState();

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 920px)' });

  // Fetch the skibusroutes data from the taxonomy.
  useEffect(() => {
    async function fetchSkibusRoutes() {
      const skibusRoutesApiUrl = `${drupalSettings.base_url}/${drupalSettings.current_language}/jsonapi/taxonomy_term/skibus_route?include=field_gpx_route&sort=weight`;
      const response = await fetch(skibusRoutesApiUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
      });

      if (response.ok) {
        const skibusResponseJson = await response.json();

        const busRoutes = skibusResponseJson.data.map((route) => {
          const description = route.attributes.description
            ? route.attributes.description.value
            : null;
          const cta = route.attributes.field_cta
            ? {
                title: route.attributes.field_cta.title,
                url: route.attributes.field_cta.url,
              }
            : null;

          const gpxId = route.relationships.field_gpx_route.data.id;
          const id = route.attributes.drupal_internal__tid;
          const color = route.attributes.field_bus_route_color ?? '#2a2e4b';

          const gpxFile = skibusResponseJson.included.find(
            (file) => file.id === gpxId
          );

          return {
            title: route.attributes.name,
            description,
            cta,
            gpxUrl: gpxFile.attributes.uri.url,
            id,
            color,
          };
        });

        setSkibusRoutes(busRoutes);
      }
    }

    fetchSkibusRoutes();
  }, []);

  const topoMapContributions = `&copy; Map data: © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors,
    <a href="https://viewfinderpanoramas.org/">SRTM</a> | Map style: © <a href="https://opentopomap.org/">OpenTopoMap</a>
    (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)`;

  function toggleShowBusRoutes() {
    if (!showAllBusRoutes) {
      setVisibleBusRoutes(skibusRoutes);
      setShowAllBusRoutes(true);
    } else {
      setVisibleBusRoutes([]);
      setShowAllBusRoutes(false);
    }
  }

  function showSpecificBusRoutes(value) {
    if (visibleBusRoutes.find((route) => route.id === value)) {
      setVisibleBusRoutes(
        [...visibleBusRoutes].filter((route) => route.id !== value)
      );
    } else {
      const newRoute = skibusRoutes.find(
        (skibusRoute) => skibusRoute.id === value
      );
      setVisibleBusRoutes([...visibleBusRoutes, newRoute]);
    }
  }

  const BusRoutes = () => {
    const map = useMap();

    if (layerGroupRef.current) {
      map.removeLayer(layerGroupRef.current);
      layerGroupRef.current = null;
    }

    layerGroupRef.current = L.layerGroup().addTo(map);

    visibleBusRoutes.forEach((route) => {
      const { gpxUrl } = route;

      const popupHtml = (
        <div className="route-popup">
          <h3>{route.title}</h3>
          {route.description && (
            <p dangerouslySetInnerHTML={{ __html: route.description }}></p>
          )}
          {route.cta && (
            <a
              href={route.cta.url}
              target="_blank"
              rel="noreferrer"
              className="button button--large-full-width external-link"
            >
              {route.cta.title}
            </a>
          )}
        </div>
      );

      const gpxElement = new L.GPX(gpxUrl, {
        async: true,
        markers: { startIcon: null, endIcon: null },

        polyline_options: {
          color: route.color,
          weight: 5,
        },
      })
        .on('mouseover', function () {
          if (!isTabletOrMobile) {
            rearrangeRoutes(route.id, this);
          }
        })
        .bindPopup(renderToString(popupHtml), {
          maxWidth: '350px',
          offset: [0, -5],
        });

      layerGroupRef.current.addLayer(gpxElement);
    });

    return null;
  };

  const ScaleBar = () => {
    const map = useMap();

    useEffect(() => {
      const scaleControl = L.control.scale({
        imperial: false,
      });

      scaleControl.addTo(map);

      return () => {
        map.removeControl(scaleControl);
      };
    }, [map]);

    return null;
  };

  const busControls = (
    <div className="bus-controls">
      <form>
        <span>
          <input
            type="checkbox"
            name="showbusroutes"
            value="showbusroutes"
            checked={showAllBusRoutes}
            onChange={toggleShowBusRoutes}
          />
          <label htmlFor="showbusroutes" onClick={toggleShowBusRoutes}>
            {Drupal.t('Show all bus routes')}
          </label>
        </span>

        {skibusRoutes.map((skibusRoute) => (
          <span key={skibusRoute.id}>
            <input
              type="checkbox"
              name={`show-route-${skibusRoute.id}`}
              onChange={() => showSpecificBusRoutes(skibusRoute.id)}
              checked={
                !!visibleBusRoutes.find((route) => route.id === skibusRoute.id)
              }
            />
            <label
              htmlFor={`show-route-${skibusRoute.id}`}
              onClick={() => showSpecificBusRoutes(skibusRoute.id)}
            >
              {skibusRoute.title}{' '}
              <span
                style={{ backgroundColor: skibusRoute.color }}
                className="skibusroute-line-marker"
              ></span>
            </label>
          </span>
        ))}
      </form>
    </div>
  );

  // Rearrange routes so that the hovered route will be rendered
  // last, and this way it'll be shown on top of other routes.
  function rearrangeRoutes(id) {
    // Prevents flickering and routes being redrawn constantly.
    if (topRoute === id) {
      return;
    }
    setTopRoute(id);
    const newRoutes = [...visibleBusRoutes];
    const itemIndex = newRoutes.findIndex((item) => item.id === id);
    const item = newRoutes.splice(itemIndex, 1)[0];
    newRoutes.push(item);
    setVisibleBusRoutes(newRoutes);
  }

  return (
    <div>
      <MapContainer
        style={{ height: '70vh' }}
        center={[latitude, longitude]}
        zoom={zoom}
        scrollWheelZoom={true}
        doubleClickZoom={false}
      >
        <LayersControl>
          <BaseLayer checked name={Drupal.t('Standard map')}>
            <TileLayer
              maxZoom={19}
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </BaseLayer>

          <BaseLayer name={Drupal.t('Topographical map')}>
            <TileLayer
              maxZoom={19}
              maxNativeZoom={15}
              url="https://tile.opentopomap.org/{z}/{x}/{y}.png"
              attribution={topoMapContributions}
            />
          </BaseLayer>
        </LayersControl>

        {skibusRoutes && skibusRoutes.length > 0 && busControls}

        <MarkerCluster markers={props.results} />

        <ScaleBar />

        <BusRoutes />
      </MapContainer>
    </div>
  );
};

TravelSearchMap.propTypes = {
  results: PropTypes.array,
};

export default TravelSearchMap;
